/**
 * DocumentsCard — Sección de carga y visualización de documentos del miembro.
 * - Documento de identidad: anverso + reverso (si es CC/TI/RC), o un solo archivo (CE/PASAPORTE/PPT)
 * - Certificado EPS / seguro de salud
 * Tanto el miembro (desde su perfil) como el director (desde el panel) pueden usarlo.
 */

import { useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
} from "@/components/ui";
import { DocumentUpload } from "@/components/ui";
import DocumentScanner from "./DocumentScanner";
import {
  FileText,
  Upload,
  Camera,
  CheckCircle,
  Loader2,
  RefreshCw,
  Heart,
  User,
  AlertTriangle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  uploadDocumentFront,
  uploadDocumentBack,
  uploadEpsDocument,
  uploadMemberPhoto,
} from "@/lib/services/storage";
import { EncryptedDocumentView } from "@/components/ui/EncryptedDocumentView";
import { useUpdatePerson } from "@/hooks/usePersons";
import type { Person, IdDocumentType } from "@/types";
import type { EncryptionMeta } from "@/lib/services/crypto";

/** Tipos de documento que son tarjeta (tienen anverso y reverso) */
const CARD_TYPES: IdDocumentType[] = ["CC", "TI", "RC"];
const ACCEPTS = "image/jpeg,image/png,image/webp,application/pdf";

interface DocumentSlotProps {
  label: string;
  url?: string;
  encryption?: EncryptionMeta | null;
  contentType?: string;
  loading: boolean;
  onSelect: (file: File) => void;
  /** Callback para abrir el scanner de cámara con autocrop */
  onCamera?: () => void;
}

function DocumentSlot({
  label,
  url,
  encryption,
  contentType,
  loading,
  onSelect,
  onCamera,
}: DocumentSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  // Archivos encriptados tienen extensión .enc, no .pdf
  const isPdf = encryption
    ? contentType === "application/pdf"
    : url?.includes(".pdf") ||
      url?.includes("%2Fpdf") ||
      url?.toLowerCase().includes("application%2Fpdf");

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // 10 MB limit
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo no puede superar 10 MB");
      return;
    }
    onSelect(file);
    e.target.value = "";
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </p>

      {/* Preview */}
      {url ? (
        <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-[3/2] flex items-center justify-center group">
          <EncryptedDocumentView
            url={url}
            encryption={encryption}
            contentType={contentType}
            alt={label}
            className="w-full h-full object-cover"
            isPdf={isPdf}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          {/* Badge de cargado */}
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full shadow">
            <CheckCircle className="w-3 h-3" />
            Subido
          </div>
          {/* Botón de remplazar */}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute bottom-2 right-2 flex items-center gap-1 bg-white/90 hover:bg-white text-gray-700 text-xs px-2 py-1 rounded-lg shadow border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <RefreshCw className="w-3 h-3" />
            Reemplazar
          </button>
        </div>
      ) : (
        <div
          className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 hover:border-primary-300 hover:bg-primary-50 aspect-[3/2] flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors group"
          onClick={() => inputRef.current?.click()}
        >
          {loading ? (
            <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
          ) : (
            <>
              <Upload className="w-8 h-8 text-gray-300 group-hover:text-primary-400 transition-colors" />
              <p className="text-xs text-gray-400 group-hover:text-primary-500 text-center px-3">
                Haz clic para subir
                <br />
                <span className="text-[10px]">JPG, PNG, PDF · máx 10 MB</span>
              </p>
            </>
          )}
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1 text-xs"
          disabled={loading}
          onClick={() => inputRef.current?.click()}
        >
          {loading ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Upload className="w-3 h-3 mr-1" />
          )}
          {url ? "Reemplazar" : "Subir archivo"}
        </Button>
        {onCamera && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1 text-xs"
            disabled={loading}
            onClick={onCamera}
          >
            <Camera className="w-3 h-3 mr-1" />
            Escanear
          </Button>
        )}
      </div>

      {/* Input archivo normal */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTS}
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
}

// ============================================================

interface DocumentsCardProps {
  person: Person;
  /** Fn de actualización alternativa. Útil para wards (usa `updateWardSafe`
   *  con la whitelist específica del guardián). Si no se pasa, usa `useUpdatePerson`. */
  updateFn?: (patch: Partial<Person>) => Promise<void>;
}

export default function DocumentsCard({
  person,
  updateFn,
}: DocumentsCardProps) {
  const updatePersonMutation = useUpdatePerson();
  const isCardType = CARD_TYPES.includes(person.idDocumentType);
  const hasPhoto = !!person.photoUrl;

  const applyUpdate = async (patch: Partial<Person>) => {
    if (updateFn) return updateFn(patch);
    await updatePersonMutation.mutateAsync({ id: person.id, data: patch });
  };

  const [loadingFront, setLoadingFront] = useState(false);
  const [loadingBack, setLoadingBack] = useState(false);
  const [loadingEps, setLoadingEps] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [scanningSlot, setScanningSlot] = useState<"front" | "back" | null>(
    null,
  );

  const handleFront = async (file: File) => {
    setLoadingFront(true);
    try {
      const result = await uploadDocumentFront(file, person.id);
      await applyUpdate({
        documentFrontUrl: result.url,
        documentFrontPath: result.path,
        documentFrontEncryption: result.encryption,
        documentFrontContentType: file.type,
      });
      toast.success("Anverso guardado");
    } catch {
      toast.error("Error al subir el anverso");
    } finally {
      setLoadingFront(false);
    }
  };

  const handleBack = async (file: File) => {
    setLoadingBack(true);
    try {
      const result = await uploadDocumentBack(file, person.id);
      await applyUpdate({
        documentBackUrl: result.url,
        documentBackPath: result.path,
        documentBackEncryption: result.encryption,
        documentBackContentType: file.type,
      });
      toast.success("Reverso guardado");
    } catch {
      toast.error("Error al subir el reverso");
    } finally {
      setLoadingBack(false);
    }
  };

  const handleEps = async (file: File) => {
    setLoadingEps(true);
    try {
      const result = await uploadEpsDocument(file, person.id);
      await applyUpdate({
        epsDocumentUrl: result.url,
        epsDocumentPath: result.path,
        epsDocumentEncryption: result.encryption,
        epsDocumentContentType: file.type,
      });
      toast.success("Certificado EPS guardado");
    } catch {
      toast.error("Error al subir el certificado");
    } finally {
      setLoadingEps(false);
    }
  };

  const handlePhoto = async (file: File) => {
    setLoadingPhoto(true);
    try {
      const result = await uploadMemberPhoto(file, person.id);
      await applyUpdate({
        photoUrl: result.url,
        photoPath: result.path,
      });
      toast.success("Foto guardada");
      return { url: result.url, path: result.path };
    } catch (e) {
      toast.error("Error al subir la foto");
      throw e;
    } finally {
      setLoadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    try {
      await applyUpdate({ photoUrl: "", photoPath: "" });
      toast.success("Foto eliminada");
    } catch {
      toast.error("No se pudo eliminar la foto");
    }
  };

  return (
    <>
      {/* Scanner modal */}
      {scanningSlot && (
        <DocumentScanner
          label={
            scanningSlot === "front"
              ? `Anverso — ${person.idDocumentType} ${person.idDocumentNumber}`
              : `Reverso — ${person.idDocumentType} ${person.idDocumentNumber}`
          }
          onCapture={scanningSlot === "front" ? handleFront : handleBack}
          onClose={() => setScanningSlot(null)}
        />
      )}

      <Card className="border-0 shadow-md overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-gray-50/50 to-blue-50/30">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-sm shadow-blue-500/25">
              <FileText className="w-4 h-4 text-white" />
            </div>
            Documentos
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-5 space-y-7">
          {/* Banner: falta foto tipo carnet */}
          {!hasPhoto && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" />
              <div>
                <p className="font-semibold">Falta la foto tipo carnet</p>
                <p className="text-xs text-amber-800/80 mt-0.5">
                  Es obligatoria para generar el carnet del club. Súbela en el
                  bloque de abajo (fondo claro, rostro visible).
                </p>
              </div>
            </div>
          )}

          {/* Foto tipo carnet */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-primary-600" />
              <p className="text-sm font-semibold text-gray-800">
                Foto tipo carnet
                <span className="ml-2 text-xs font-normal text-gray-400">
                  (fondo claro, rostro visible)
                </span>
              </p>
            </div>
            <div className="max-w-xs">
              <DocumentUpload
                currentUrl={person.photoUrl || null}
                title="Foto tipo carnet"
                description="Rostro centrado, buena iluminación"
                icon={<User className="w-6 h-6 text-primary-500" />}
                onUpload={handlePhoto}
                onRemove={removePhoto}
                isUploading={loadingPhoto}
              />
            </div>
          </div>

          {/* Documento de identidad */}
          <div className="pt-4 border-t">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-blue-500" />
              <p className="text-sm font-semibold text-gray-800">
                Documento de identidad
                <span className="ml-2 text-xs font-normal text-gray-400">
                  ({person.idDocumentType} · {person.idDocumentNumber})
                </span>
              </p>
            </div>

            {isCardType ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DocumentSlot
                  label="Anverso (frente)"
                  url={person.documentFrontUrl}
                  encryption={
                    person.documentFrontEncryption as EncryptionMeta | undefined
                  }
                  contentType={person.documentFrontContentType}
                  loading={loadingFront}
                  onSelect={handleFront}
                  onCamera={() => setScanningSlot("front")}
                />
                <DocumentSlot
                  label="Reverso (dorso)"
                  url={person.documentBackUrl}
                  encryption={
                    person.documentBackEncryption as EncryptionMeta | undefined
                  }
                  contentType={person.documentBackContentType}
                  loading={loadingBack}
                  onSelect={handleBack}
                  onCamera={() => setScanningSlot("back")}
                />
              </div>
            ) : (
              <DocumentSlot
                label="Documento (imagen o PDF)"
                url={person.documentFrontUrl}
                encryption={
                  person.documentFrontEncryption as EncryptionMeta | undefined
                }
                contentType={person.documentFrontContentType}
                loading={loadingFront}
                onSelect={handleFront}
                onCamera={() => setScanningSlot("front")}
              />
            )}
          </div>

          {/* EPS */}
          <div className="pt-4 border-t">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-4 h-4 text-rose-500" />
              <p className="text-sm font-semibold text-gray-800">
                Certificado EPS / Seguro de salud
                {person.healthInsurance && (
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    ({person.healthInsurance})
                  </span>
                )}
              </p>
            </div>
            <DocumentSlot
              label="Certificado vigente (imagen o PDF)"
              url={person.epsDocumentUrl}
              encryption={
                person.epsDocumentEncryption as EncryptionMeta | undefined
              }
              contentType={person.epsDocumentContentType}
              loading={loadingEps}
              onSelect={handleEps}
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
}
